/**
 * Locale-aware formatting utilities that respect platform theme settings.
 * Uses the resolved theme's locale settings for consistent formatting across the app.
 */

export interface LocaleSettings {
  dateFormat: string;
  timeFormat: string;
  thousandsSeparator: string;
  decimalSeparator: string;
  currencySymbol: string;
  currencyFormat: string;
}

const DEFAULT_LOCALE: LocaleSettings = {
  dateFormat: 'DD.MM.YYYY',
  timeFormat: 'HH:mm',
  thousandsSeparator: '.',
  decimalSeparator: ',',
  currencySymbol: '\u20ac',
  currencyFormat: 'value_first',
};

let currentLocale: LocaleSettings = { ...DEFAULT_LOCALE };

/** Update the current locale settings (called when theme loads) */
export function setLocaleSettings(settings: Partial<LocaleSettings>): void {
  currentLocale = {
    dateFormat: settings.dateFormat || DEFAULT_LOCALE.dateFormat,
    timeFormat: settings.timeFormat || DEFAULT_LOCALE.timeFormat,
    thousandsSeparator: settings.thousandsSeparator || DEFAULT_LOCALE.thousandsSeparator,
    decimalSeparator: settings.decimalSeparator || DEFAULT_LOCALE.decimalSeparator,
    currencySymbol: settings.currencySymbol || DEFAULT_LOCALE.currencySymbol,
    currencyFormat: settings.currencyFormat || DEFAULT_LOCALE.currencyFormat,
  };
}

/** Get current locale settings */
export function getLocaleSettings(): LocaleSettings {
  return { ...currentLocale };
}

/** Check whether the current locale uses 24-hour time format. */
export function is24HourFormat(): boolean {
  return currentLocale.timeFormat !== 'hh:mm A';
}

/**
 * Format a date string or Date object according to locale settings.
 */
export function formatDate(input: string | Date | null | undefined): string {
  if (!input) return '';
  const date = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());

  switch (currentLocale.dateFormat) {
    case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
    case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
    case 'DD.MM.YYYY':
    default: return `${day}.${month}.${year}`;
  }
}

/**
 * Format a time from a Date object according to locale settings.
 */
export function formatTime(input: string | Date | null | undefined): string {
  if (!input) return '';
  const date = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(date.getTime())) return '';

  const hours24 = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  switch (currentLocale.timeFormat) {
    case 'hh:mm A': {
      const period = hours24 >= 12 ? 'PM' : 'AM';
      const hours12 = hours24 % 12 || 12;
      return `${String(hours12).padStart(2, '0')}:${minutes} ${period}`;
    }
    case 'HH:mm:ss':
      return `${String(hours24).padStart(2, '0')}:${minutes}:${seconds}`;
    case 'HH:mm':
    default:
      return `${String(hours24).padStart(2, '0')}:${minutes}`;
  }
}

/**
 * Format a date and time together as an object.
 */
export function formatDateTime(input: string | Date | null | undefined): { date: string; time: string } {
  return { date: formatDate(input), time: formatTime(input) };
}

/**
 * Format a date and time together as a single string (e.g. "09.04.2026 14:30").
 * Drop-in replacement for `date.toLocaleString()`.
 */
export function formatDateTimeStr(input: string | Date | null | undefined): string {
  if (!input) return '';
  const d = formatDate(input);
  const t = formatTime(input);
  if (!d && !t) return '';
  return `${d} ${t}`;
}

/**
 * Format a short date label (e.g. "Apr 9") respecting locale settings.
 * Replacement for `date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })`.
 */
export function formatShortDate(input: string | Date | null | undefined): string {
  if (!input) return '';
  const date = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(date.getTime())) return '';

  const day = date.getDate();
  const monthNames = getMonthShortNames();
  const month = monthNames[date.getMonth()];
  return `${month} ${day}`;
}

/**
 * Format a number according to locale settings.
 */
export function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);

  const isNegative = num < 0;
  const absNum = Math.abs(num);
  const parts = absNum.toString().split('.');
  const intPart = parts[0];
  const decPart = parts[1];

  // Add thousands separators
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, currentLocale.thousandsSeparator);

  let result = formatted;
  if (decPart !== undefined) {
    result += currentLocale.decimalSeparator + decPart;
  }

  return isNegative ? `-${result}` : result;
}

/**
 * Derive a BCP-47 locale tag from the current date format setting.
 * Used for locale-aware month/weekday names via Intl.DateTimeFormat.
 */
function deriveBcp47Locale(): string {
  switch (currentLocale.dateFormat) {
    case 'MM/DD/YYYY': return 'en-US';
    case 'DD/MM/YYYY': return 'en-GB';
    case 'YYYY-MM-DD': return 'sv-SE'; // ISO format common in Scandinavian locales
    case 'DD.MM.YYYY':
    default: return 'de-DE';
  }
}

/** Get localized month names (long). */
export function getMonthNames(): string[] {
  const locale = deriveBcp47Locale();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(2024, i, 1);
    return d.toLocaleDateString(locale, { month: 'long' });
  });
}

/** Get localized short month names. */
export function getMonthShortNames(): string[] {
  const locale = deriveBcp47Locale();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(2024, i, 1);
    return d.toLocaleDateString(locale, { month: 'short' });
  });
}

/** Get localized weekday labels (short), starting from Sunday (index 0). */
export function getWeekdayLabels(): string[] {
  const locale = deriveBcp47Locale();
  // Jan 7 2024 = Sunday
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(2024, 0, 7 + i);
    return d.toLocaleDateString(locale, { weekday: 'short' });
  });
}

/** Get localized weekday names (long), starting from Sunday (index 0). */
export function getWeekdayLongNames(): string[] {
  const locale = deriveBcp47Locale();
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(2024, 0, 7 + i);
    return d.toLocaleDateString(locale, { weekday: 'long' });
  });
}

/**
 * Format a currency value according to locale settings.
 */
export function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);

  const formattedNum = formatNumber(num);

  if (currentLocale.currencyFormat === 'symbol_first') {
    return `${currentLocale.currencySymbol} ${formattedNum}`;
  }
  return `${formattedNum} ${currentLocale.currencySymbol}`;
}
